import React, { useEffect, useState, useCallback } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { FaPlus, FaPencilAlt, FaTrash, FaHandshake, FaBuilding } from "react-icons/fa";
import { db } from "../../firebase";
import AdminModal from "../../components/AdminModal";
import DataTable from "../../components/DataTable";
import FormField from "../../components/FormField";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import { useToast } from "../../components/Toast";
import { colors, spacing, typography, radius, shadows, pageContainer, pageTitle, pageSubtitle } from "../../utils/designTokens";

/**
 * Pantalla SuperAdmin: gestion de administradoras partner.
 *
 * Una "partner" es una administradora externa a la que Habitia le presta
 * servicios (canal indirecto). Los edificios que ella administra se marcan
 * con building.partnerId, lo que activa co-branding en los mensajes de
 * WhatsApp ("[Partner] (gestionado por Habitia)").
 *
 * MVP:
 *   - Lista las partners cargadas en `partners` collection.
 *   - Crear / editar partner con datos basicos.
 *   - Asignar/desasignar edificios al partner desde el mismo modal
 *     (multi-select de buildings disponibles).
 *
 * Lo que NO incluye este MVP (queda para sprint 3):
 *   - Branding visual custom por partner (logos, colores).
 *   - Permisos diferenciados para usuarios de la partner.
 *   - Edicion de templates per-partner.
 */

const initialForm = {
  name: "",
  legalName: "",
  rut: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  active: true,
};

const Partners = () => {
  const { showToast } = useToast();
  const [partners, setPartners] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [selectedBuildings, setSelectedBuildings] = useState(new Set());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [partnersSnap, buildingsSnap] = await Promise.all([
        getDocs(collection(db, "partners")),
        getDocs(collection(db, "building")),
      ]);
      const partnersData = partnersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const buildingsData = buildingsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Contar edificios asignados por partner
      const counts = {};
      buildingsData.forEach((b) => {
        if (b.partnerId) {
          counts[b.partnerId] = (counts[b.partnerId] || 0) + 1;
        }
      });
      partnersData.forEach((p) => {
        p._buildingCount = counts[p.id] || 0;
      });
      setPartners(partnersData);
      setBuildings(buildingsData);
    } catch (e) {
      console.error("Partners fetchAll error:", e);
      showToast("Error cargando partners", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setSelectedBuildings(new Set());
    setModalOpen(true);
  };

  const openEdit = (partner) => {
    setEditing(partner);
    setForm({
      name: partner.name || "",
      legalName: partner.legalName || "",
      rut: partner.rut || "",
      contactName: partner.contactName || "",
      contactEmail: partner.contactEmail || "",
      contactPhone: partner.contactPhone || "",
      active: partner.active !== false,
    });
    const assigned = new Set(
      buildings.filter((b) => b.partnerId === partner.id).map((b) => b.id)
    );
    setSelectedBuildings(assigned);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(initialForm);
    setSelectedBuildings(new Set());
  };

  const toggleBuilding = (buildingId) => {
    setSelectedBuildings((prev) => {
      const next = new Set(prev);
      if (next.has(buildingId)) next.delete(buildingId);
      else next.add(buildingId);
      return next;
    });
  };

  const handleSave = async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      showToast("El nombre comercial es obligatorio", "error");
      return;
    }
    setSaving(true);
    try {
      // Slug del id si es nuevo, sino usar el id existente
      const partnerId =
        editing?.id ||
        trimmedName
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 40) || `partner-${Date.now()}`;

      const payload = {
        name: trimmedName,
        legalName: form.legalName.trim(),
        rut: form.rut.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim().toLowerCase(),
        contactPhone: form.contactPhone.trim(),
        active: !!form.active,
        updatedAt: serverTimestamp(),
      };
      if (!editing) payload.createdAt = serverTimestamp();

      // 1. Crear/actualizar el partner
      await setDoc(doc(db, "partners", partnerId), payload, { merge: true });

      // 2. Sincronizar asignacion de buildings con la nueva seleccion
      // - Desasignar los que estaban antes y ya no estan seleccionados
      // - Asignar los seleccionados (set partnerId al partner)
      const batch = writeBatch(db);
      const previouslyAssignedIds = new Set(
        buildings.filter((b) => b.partnerId === partnerId).map((b) => b.id)
      );

      buildings.forEach((b) => {
        const wasAssigned = previouslyAssignedIds.has(b.id);
        const willBeAssigned = selectedBuildings.has(b.id);
        if (wasAssigned && !willBeAssigned) {
          // Quitar partnerId
          batch.update(doc(db, "building", b.id), { partnerId: null });
        } else if (!wasAssigned && willBeAssigned) {
          // Asignar partnerId. Si el building tenia otro partner, lo sobreescribe.
          batch.update(doc(db, "building", b.id), { partnerId });
        }
      });
      await batch.commit();

      showToast(editing ? "Partner actualizado" : "Partner creado", "success");
      closeModal();
      await fetchAll();
    } catch (e) {
      console.error("Partners save error:", e);
      showToast(`Error: ${e.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (partner) => {
    if (partner._buildingCount > 0) {
      showToast(
        `No se puede eliminar: tiene ${partner._buildingCount} edificio(s) asignado(s). Desasignalos primero.`,
        "error"
      );
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Eliminar "${partner.name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await deleteDoc(doc(db, "partners", partner.id));
      showToast("Partner eliminado", "success");
      await fetchAll();
    } catch (e) {
      console.error("Partners delete error:", e);
      showToast(`Error: ${e.message}`, "error");
    }
  };

  const columns = [
    {
      key: "name",
      label: "Nombre comercial",
      render: (val, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 400, color: colors.gray900 }}>{val}</span>
          {row.legalName && (
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>
              {row.legalName}
            </span>
          )}
        </div>
      ),
    },
    { key: "rut", label: "RUT", render: (v) => v || "—" },
    {
      key: "contactName",
      label: "Contacto",
      render: (val, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span>{val || "—"}</span>
          {row.contactEmail && (
            <span style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>
              {row.contactEmail}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "_buildingCount",
      label: "Edificios",
      align: "center",
      render: (val) => (
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 10px",
          background: val > 0 ? colors.primaryLighter : colors.gray100,
          color: val > 0 ? colors.primaryDark : colors.gray600,
          borderRadius: radius.full,
          fontSize: typography.sizes.sm,
        }}>
          <FaBuilding size={11} />
          {val}
        </span>
      ),
    },
    {
      key: "active",
      label: "Estado",
      align: "center",
      render: (v) => (
        <span style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: radius.full,
          fontSize: typography.sizes.sm,
          background: v !== false ? colors.successLight : colors.gray100,
          color: v !== false ? colors.successDark : colors.gray600,
        }}>
          {v !== false ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      key: "_actions",
      label: "",
      align: "right",
      width: "120px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={(e) => { e.stopPropagation(); openEdit(row); }}
            style={iconBtnStyle}
            aria-label="Editar"
            title="Editar"
          >
            <FaPencilAlt size={12} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            style={{ ...iconBtnStyle, color: colors.error }}
            aria-label="Eliminar"
            title="Eliminar"
          >
            <FaTrash size={12} />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <Loader />;
  }

  return (
    <div style={pageContainer}>
      <div style={headerStyle}>
        <div>
          <h1 style={pageTitle}>Administradoras partner</h1>
          <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
            Gestiona las administradoras externas a las que Habitia presta servicios.
            Los edificios asignados a un partner reciben mensajes con co-branding.
          </p>
        </div>
        <button onClick={openCreate} style={primaryBtnStyle}>
          <FaPlus size={12} />
          Nuevo partner
        </button>
      </div>

      {partners.length === 0 ? (
        <EmptyState
          icon={FaHandshake}
          title="No hay partners cargados"
          description="Cuando crees una administradora partner y le asignes edificios, los mensajes a esos residentes van a incluir el co-branding del partner."
          action={{ label: "Crear primer partner", onClick: openCreate }}
        />
      ) : (
        <DataTable columns={columns} data={partners} onRowClick={openEdit} />
      )}

      <AdminModal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? `Editar ${editing.name}` : "Nuevo partner"}
        size="wide"
        icon={<FaHandshake size={20} color={colors.primary} />}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={closeModal} disabled={saving} style={ghostBtnStyle}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={primaryBtnStyle}>
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear partner"}
            </button>
          </div>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[4] }}>
          <FormField label="Nombre comercial" htmlFor="p_name" required>
            <input
              id="p_name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder='Ej: "Inmobiliaria Sur"'
              style={inputStyle}
              autoFocus
            />
          </FormField>
          <FormField label="Razon social" htmlFor="p_legal" hint="Nombre legal completo (opcional)">
            <input
              id="p_legal"
              type="text"
              value={form.legalName}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              placeholder='Ej: "Inmobiliaria Sur SRL"'
              style={inputStyle}
            />
          </FormField>
          <FormField label="RUT" htmlFor="p_rut">
            <input
              id="p_rut"
              type="text"
              value={form.rut}
              onChange={(e) => setForm({ ...form, rut: e.target.value })}
              placeholder="12 digitos"
              style={inputStyle}
            />
          </FormField>
          <FormField label="Estado" htmlFor="p_active">
            <select
              id="p_active"
              value={form.active ? "true" : "false"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "true" })}
              style={inputStyle}
            >
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </FormField>
          <FormField label="Nombre de contacto" htmlFor="p_contact">
            <input
              id="p_contact"
              type="text"
              value={form.contactName}
              onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Email de contacto" htmlFor="p_email">
            <input
              id="p_email"
              type="email"
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Telefono de contacto" htmlFor="p_phone" fullWidth>
            <input
              id="p_phone"
              type="tel"
              value={form.contactPhone}
              onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
              placeholder="+598 99 ..."
              style={inputStyle}
            />
          </FormField>
        </div>

        <div style={{ marginTop: spacing[6], paddingTop: spacing[4], borderTop: `1px solid ${colors.gray200}` }}>
          <h3 style={{ fontSize: typography.sizes.lg, fontWeight: 400, marginBottom: spacing[1] }}>
            Edificios asignados
          </h3>
          <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: spacing[3] }}>
            Marcalos para que los mensajes a sus residentes incluyan el co-branding del partner.
            Si un edificio pertenece a otro partner, esta seleccion lo reasigna a este.
          </p>
          {buildings.length === 0 ? (
            <p style={{ fontSize: typography.sizes.sm, color: colors.gray400 }}>
              No hay edificios cargados todavia.
            </p>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: spacing[2],
              maxHeight: 280,
              overflowY: "auto",
              padding: spacing[2],
              border: `1px solid ${colors.gray200}`,
              borderRadius: radius.md,
            }}>
              {buildings.map((b) => {
                const checked = selectedBuildings.has(b.id);
                const otherPartner =
                  b.partnerId && b.partnerId !== editing?.id ? b.partnerId : null;
                return (
                  <label
                    key={b.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: spacing[2],
                      borderRadius: radius.sm,
                      cursor: "pointer",
                      background: checked ? colors.primaryLighter : "transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleBuilding(b.id)}
                      style={{ cursor: "pointer" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: typography.sizes.base, color: colors.gray900 }}>
                        {b.name || `Edificio ${b.buildingid}`}
                      </div>
                      {otherPartner && (
                        <div style={{ fontSize: 11, color: colors.warning }}>
                          Asignado a otro partner: {otherPartner}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </AdminModal>
    </div>
  );
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: spacing[5],
  gap: spacing[4],
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray900,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.white,
  background: colors.primary,
  border: "none",
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: shadows.xs,
};

const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray700,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const iconBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  background: colors.gray100,
  border: "none",
  borderRadius: radius.sm,
  cursor: "pointer",
  color: colors.gray700,
};

export default Partners;
